import mapKnownValues from "../utils/mapKnownValues";
import DestinationAccount, { AccountType } from "./destinationAccount";
import SourceAccount from "./sourceAccount";

export enum TransferAccountType {
  EMPLOYER_401k = "401k",
  EMPLOYER_403b = "403b",
  EMPLOYER_457 = "457",
  IRA = "IRA",
  ROTH_IRA = "ROTH_IRA",
  SIMPLE_IRA = "SIMPLE_IRA",
  SEP_IRA = "SEP_IRA",
  ANNUITY = "ANNUITY",
}

export enum MartialStatusResponse {
  YES = "YES",
  NO = "NO",
}

export type UserResponse = {
  spouseEmail?: string;
  spouseName?: string;
  spousePhoneNumber?: string;
  spouseDateOfBirth?: string;
  userId?: string;
  clientUserId: string; // REQUIRED
  created?: string | null;
  email: string; // REQUIRED
  phoneNumber?: string;
  firstName: string; // REQUIRED
  middleName?: string;
  lastName: string; // REQUIRED
  suffix?: string;
  citizenship?: string;
  dateOfBirth?: string;
  married?: MartialStatusResponse;
  address?: {
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
};

export type TransferIntentDestination = {
  type: TransferAccountType;
  acceptsRothFunds: boolean;
  accountNumber?: string;
  custodianName?: string;
  isPrimary: boolean;
  planName?: string;
  planNumber?: string;
  planSponsor?: string;
  providerName: string;
};

export type PartialTransfer = {
  transferType: "AMOUNT" | "PERCENTAGE";
  preTax?: number;
  roth?: number;
  afterTax?: number;
};

export type TransferIntentSourceAccount = {
  type: TransferAccountType;
  hasRothFunds: boolean;
  planName?: string;
  planNumber?: string;
  planSponsor?: string;
  providerName: string;
  partialTransfer?: PartialTransfer;
};

export type TransferIntentResponse = {
  subClient?: {
    name?: string;
    subClientId?: string;
  };
  destinationAccounts: TransferIntentDestination[];
  transferIntentReference: string;
  status: string;
  created: string;
  sourceAccounts?: TransferIntentSourceAccount[];
  userIdentity: {
    email: string;
    phoneNumber: string;
    userDetails: UserResponse;
  };
  version?: string;
  processingInstructions?: {
    checkMemo?: string;
    identityVerificationRequired?: boolean;
    spousalConsentRequired?: boolean;
  };
};

export type PrimaryDestinationChoice = TransferIntentDestination & {
  isOpened: boolean;
};

const transformPrimaryDestination = (
  transferIntentDestination: TransferIntentDestination,
): PrimaryDestinationChoice => {
  return {
    ...transferIntentDestination,
    isOpened: transferIntentDestination.type.toLowerCase().includes("ira")
      ? !!transferIntentDestination.accountNumber
      : true,
  };
};

export class TransferIntent {
  transferIntentId = "";
  status = "";
  user: TransferIntentResponse["userIdentity"]["userDetails"] | undefined = undefined;
  sourceAccounts?: TransferIntentSourceAccount[] = undefined;
  destinationAccounts?: TransferIntentDestination[] = [];
  userEmail = "";
  userPhoneNumber = "";
  version: string | null = null;
  hasPrimaryDestination = false;
  primaryDestination: PrimaryDestinationChoice | null = null;
  subClient: {
    name?: string;
    subClientId?: string;
  } = {};
  transferType: "AMOUNT" | "PERCENTAGE" | "FULL";
  isAnnuityTransfer: boolean | null = null;
  processingInstructions?: {
    checkMemo?: string;
    identityVerificationRequired?: boolean;
    spousalConsentRequired?: boolean;
  };

  constructor(response: TransferIntentResponse) {
    mapKnownValues(this, response);
    this.transferIntentId = response.transferIntentReference;
    this.user = response.userIdentity.userDetails;
    this.userEmail = response.userIdentity.email;
    this.userPhoneNumber = response.userIdentity.phoneNumber;
    const primaryDestinations = response.destinationAccounts.filter((d) => d.isPrimary);
    const hasPrimaryDestination = primaryDestinations.length === 1;
    this.primaryDestination = hasPrimaryDestination
      ? transformPrimaryDestination(response.destinationAccounts[0])
      : null;
    this.hasPrimaryDestination = hasPrimaryDestination;
    this.isAnnuityTransfer = primaryDestinations[0]?.type === TransferAccountType.ANNUITY;
    this.transferType =
      primaryDestinations[0]?.type === TransferAccountType.ANNUITY &&
      response?.sourceAccounts &&
      response.sourceAccounts[0] &&
      response?.sourceAccounts[0].partialTransfer &&
      response?.sourceAccounts[0].partialTransfer.transferType
        ? response?.sourceAccounts[0].partialTransfer.transferType
        : "FULL";
  }
  static convertIntentToDestination(details: TransferIntentDestination): DestinationAccount {
    const isIRA = details.type.toLowerCase().includes("ira") || !!details.accountNumber;
    const isAnnuity = details.type === TransferAccountType.ANNUITY;
    return new DestinationAccount({
      ...details,
      planNumber: isAnnuity ? details.accountNumber : undefined,
      destinationAccountType: isAnnuity ? AccountType.ANNUITY : isIRA ? AccountType.ira : AccountType.employerSponsored,
      traditionalIraAccountNumber: isAnnuity ? null : details.accountNumber || null,
      statementSkipped: "TRUE",
      yetToOpen: isIRA ? !details.accountNumber : false,
    });
  }
  static convertIntentToSourceAccounts(details: TransferIntentSourceAccount[]): SourceAccount[] {
    return details.map((s) => new SourceAccount(s));
  }
}
